import { computed } from 'vue'

/**
 * A writable computed bound to an optional route param, so a tab is a path segment:
 * linkable, and it survives reloads. Writing the default value drops the segment.
 *
 *   // pages/app/emails/[groupAlias]/[emailAlias]/[[tab]].vue
 *   const tab = useRouteTab('tab', 'preview')
 *   <AppTabs v-model="tab" :tabs="tabItems" />
 *   // …/emails/foo/bar/analytics
 *
 * The page must keep one instance across tab changes, or every switch remounts it and
 * drops its state. Give the page a key that leaves the tab param out:
 *
 *   definePageMeta({ key: (route) => `email-${route.params.emailAlias}` })
 */
export function useRouteTab(param = 'tab', defaultValue = '') {
  const route = useRoute()
  const router = useRouter()

  return computed<string>({
    get() {
      const v = route.params[param]
      const val = Array.isArray(v) ? v[0] : v
      return val || defaultValue
    },
    set(val) {
      const params: Record<string, string | string[]> = { ...route.params }
      params[param] = !val || val === defaultValue ? '' : val
      // replace (not push) so tab switches don't stack history entries
      router.replace({ params, query: route.query })
    },
  })
}
